import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader, Section } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { SearchInput } from '@/components/ui/Input'
import { useTags, useBookmarkedTags } from '@/hooks/useTags'
import { cn } from '@/lib/utils'
import type { Tag } from '@/lib/pocketbase'

export function TagsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''

  const { data: tags, isLoading } = useTags({ search })
  const { data: bookmarkedTags } = useBookmarkedTags()

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    setSearchParams(params)
  }

  const bookmarkedIds = new Set(bookmarkedTags?.filter((t): t is Tag => t !== undefined).map((t) => t.id) || [])

  return (
    <div>
      <PageHeader
        title="แท็ก"
        description="เรียกดูและกรองประกาศตามแท็ก"
      />

      {/* Search */}
      <div className="mb-6">
        <SearchInput
          placeholder="ค้นหาแท็ก..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Bookmarked tags */}
      {bookmarkedTags && bookmarkedTags.length > 0 && (
        <Section title="แท็กของคุณ" description="แท็กที่คุณติดตาม" className="mb-8">
          <div className="flex flex-wrap gap-3">
            {bookmarkedTags.filter((tag): tag is Tag => tag !== undefined).map((tag) => (
              <TagChip key={tag.id} tag={tag} isBookmarked size="lg" />
            ))}
          </div>
        </Section>
      )}

      {/* All tags */}
      <Section title="แท็กทั้งหมด" description={`${tags?.totalItems || 0} แท็กที่พร้อมใช้งาน`}>
        {isLoading ? (
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="h-8 w-24 bg-slate-200 rounded-full animate-pulse" />
            ))}
          </div>
        ) : tags?.items.length === 0 ? (
          <Card className="p-8 text-center">
            <Icon icon="solar:tag-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900">ไม่พบแท็ก</h3>
            <p className="text-sm text-slate-500 mt-1">
              ลองปรับคำค้นหาของคุณ
            </p>
          </Card>
        ) : (
          <div className="flex flex-wrap gap-3">
            {tags?.items.map((tag) => (
              <TagChip
                key={tag.id}
                tag={tag}
                isBookmarked={bookmarkedIds.has(tag.id)}
                showCount
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  )
}

function TagChip({
  tag,
  isBookmarked,
  size = 'md',
  showCount,
}: {
  tag: Tag
  isBookmarked?: boolean
  size?: 'md' | 'lg'
  showCount?: boolean
}) {
  return (
    <Link
      to={`/tags/${tag.id}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border transition-all',
        'font-display',
        'hover:border-blue-300 hover:bg-blue-50',
        isBookmarked
          ? 'bg-blue-50 border-blue-200 text-blue-700'
          : 'bg-white border-slate-200 text-slate-700',
        size === 'lg' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-sm'
      )}
    >
      <Icon
        icon={isBookmarked ? 'solar:bookmark-bold' : 'solar:tag-line-duotone'}
        className={cn('w-3.5 h-3.5', isBookmarked ? 'text-blue-600' : 'text-slate-400')}
      />
      <span>{tag.name}</span>
      {showCount && tag.tender_count !== undefined && (
        <span className="text-slate-400">({tag.tender_count})</span>
      )}
    </Link>
  )
}
